import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'src/sensors/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import {
    SensorsAccessoriesPageData,
} from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { RuntimeContext } from 'src/runtimeContext';
import { isRecommendedAccessory } from 'src/utils/isRecommendedAccessory.ts';
import { calculateTotals } from 'src/sensors/checkout/CalculateTotals.ts';
import { addToCart } from 'src/utils/addToCart.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { SelectedProducts } from 'src/modules/common/components/SelectedProducts/SelectedProducts';
import { prepareSensorAttributes } from 'src/sensors/list/prepareSensorAttributes.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { useImperial } from 'src/sensors/hooks/useImperial';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Device } from 'src/common/Device/Device';
import { Price } from 'src/common/Price/Price';
import { Counter } from 'src/common/Counter/Counter';
import { SubTotal } from 'src/common/SubTotal/SubTotal';
import { Total } from 'src/common/Total/Total';
import { checkoutViewOnly } from 'src/utils/isCheckoutEnabled';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification';
import { useLocationState } from 'src/sensors/useLocationState.ts';
import { SensorExportExcel } from 'src/sensors/checkout/SensorExportExcel.tsx';

export const SensorsCheckoutPage = ({ data, searchParams } : {
    data: SensorsAccessoriesPageData;
    searchParams: URLSearchParams;
}) => {
    const {
        sensorLabel,
        page,
    } = useParams();

    if (!sensorLabel) {
        throw new Error('No Sensor selected');
    }

    const [locationState, setLocationState] = useLocationState();
    const { userSettings, currency } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const navigate = useNavigate();

    const setSensorQty = useCallback((qty: number) => {
        setLocationState({
            ...locationState,
            qty,
        });
    }, [locationState, setLocationState]);

    const setAccessoryQty = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const deleteAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories
                .filter((selectedLabel) => selectedLabel.label !== label),
        });
    }, [locationState, setLocationState]);

    const checkoutHandler = useCallback(() => {
        if (!data) {
            return;
        }
        const {
            qty,
        } = locationState;
        addToCart([
            {
                label: sensorLabel,
                qty,
            },
            ...locationState.selectedAccessories,
        ]);
        setLocationState({
            ...locationState,
            sent: true,
        });
    }, [locationState, setLocationState, sensorLabel, data]);

    const selectMoreProductsHandler = useCallback(() => {
        navigate({
            pathname: PATHS.Home,
        });
    }, [navigate]);

    const { isImperial } = useImperial();

    const { sensor } = data.accessories;

    const {
        accessories,
        accessoriesSubtotal,
        currentCurrency,
        subtotal,
        total,
        pricePerUnit,
    } = useMemo(() => calculateTotals(
        data.accessories,
        userSettings,
        locationState,
        currency,
    ), [locationState, currency, data.accessories, userSettings]);

    const isCheckoutViewOnly = checkoutViewOnly(userSettings.shoppingCenter);

    const navigation = (
        <NavigationContainer className="vss-checkout-page__navigation-container" isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: accessories.length === 0 ? [
                        PATHS.Sensors,
                        page,
                    ].join('/') : [
                        PATHS.Accessories,
                        encodeURIComponent(sensorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <SensorExportExcel
                locationState={locationState}
                accessories={accessories}
                userSettings={userSettings}
                isImperial={isImperial}
                sensor={sensor}
            />

            <Button
                onClick={checkoutHandler}
                disabled={locationState.sent || isCheckoutViewOnly}
                size="lg"
                icon={(
                    <div className="vss-checkout-page__navigation-icon">
                        <ArrowRightIcon/>
                    </div>
                )}
            >
                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
            </Button>
        </NavigationContainer>
    );

    if (locationState.sent) {
        return (
            <MainLayout>
                <MainLayoutHeader titleId={MESSAGES.selectedProductsTitle} navigation={null} breadcrumbs={null}/>

                <SelectedProducts onButtonClick={selectMoreProductsHandler}/>
            </MainLayout>
        );
    }

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.checkoutPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={null}
                />

                {isCheckoutViewOnly && (
                    <InlineNotification
                        type="warning"
                        message={<I18n messageId={MESSAGES.pleaseContact}/>}
                    />
                )}

                <div className="vss-sensors-checkout-page">
                    <h2 className="vss-sensors-checkout-page__title">
                        <I18n messageId={MESSAGES.checkoutSensorsTitle}/>
                    </h2>

                    <Device
                        defaultImage={<ValveIcon/>}
                        product={sensor}
                        attributes={prepareSensorAttributes(
                            sensor,
                            userSettings.currentRegion === RegionsEnum.Am,
                            isImperial,
                            locationState.selectedFlowRange,
                        )}
                        price={isCheckoutViewOnly ? '\u2014' : (
                            <Price
                                currency={currentCurrency}
                                price={pricePerUnit}
                                locale={userSettings.currentLocale}
                            />
                        )}
                        actions={<Counter value={locationState.qty} onChange={setSensorQty}/>}
                    />

                    <SubTotal
                        isMobile={isMobile}
                        price={isCheckoutViewOnly ? null : subtotal}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {accessories.length > 0 && (
                        <>
                            <h2 className="vss-sensors-checkout-page__title">
                                <I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>
                            </h2>

                            {accessories
                                .map(({ item }) => {
                                    const stateRow = locationState
                                        .selectedAccessories.find(({ label }) => label === item.Label);
                                    const qty = stateRow?.qty ?? 1;
                                    const price = getPriceValue(
                                        item,
                                        userSettings.shoppingCenter as keyof typeof item,
                                        currency,
                                    );
                                    return (
                                        <Accessory
                                            chip={isRecommendedAccessory(item.zOption)
                                                ? <I18n messageId={MESSAGES.recommended}/> : null}
                                            accessory={item}
                                            key={`${item.Label}-${item.ID}`}
                                            price={isCheckoutViewOnly ? '\u2014' : (
                                                <Price
                                                    price={price ? price.value : 0}
                                                    currency={currentCurrency}
                                                    locale={userSettings.currentLocale}
                                                />
                                            )}
                                        >
                                            <div className="vss-sensors-checkout-page__action-wrapper">
                                                <div className="vss-checkout-page__price-wrapper">
                                                    <Button
                                                        className="vss-checkout-page__button"
                                                        kind="ghost"
                                                        iconOnly={true}
                                                        onClick={() => deleteAccessory(item.Label)}
                                                    >
                                                        <div className="vss-checkout-page__icon-wrapper">
                                                            <DeleteIcon/>
                                                        </div>
                                                    </Button>
                                                </div>

                                                <div className="vss-checkout-page__counter">
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                </div>
                                            </div>
                                        </Accessory>
                                    );
                                })}

                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : accessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />

                        </>
                    )}

                    <Total
                        price={isCheckoutViewOnly ? null : total}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {!isMobile && (
                        <NavigationContainer isMobile={isMobile}>
                            <Button
                                className="vss-checkout-page__navigation-button"
                                onClick={checkoutHandler}
                                disabled={locationState.sent || isCheckoutViewOnly}
                                size="lg"
                                icon={(
                                    <div className="vss-checkout-page__navigation-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                )}
                            >
                                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
                            </Button>
                        </NavigationContainer>
                    )}
                </div>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
